import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { CleaningTaskWithRelations } from "@/types/cleaning";

export const useUpdateCleaningTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      propertyId,
      cleanerId,
      assigneeUserId,
      assignedType,
      coordinatorUserId,
      checklistId,
      assignments,
      includeInventoryCheck,
      scheduledDate,
      notes,
      status,
      overrideCheckInTime,
      overrideCheckOutTime,
      scheduledStartAt,
      scheduledEndAt,
      guestCount,
    }: {
      taskId: string;
      propertyId: string;
      cleanerId: string | null;
      assigneeUserId?: string | null;
      assignedType?: "owner" | "cleaner" | "unassigned" | "team";
      coordinatorUserId?: string | null;
      checklistId: string | null;
      assignments?: Array<{ checklistId: string; assignedType: "owner" | "team" | "cleaner"; cleanerId: string | null; assigneeUserId: string | null }>;
      includeInventoryCheck?: boolean;
      scheduledDate: string;
      notes: string | null;
      status: string;
      overrideCheckInTime?: string | null;
      overrideCheckOutTime?: string | null;
      scheduledStartAt?: string | null;
      scheduledEndAt?: string | null;
      /** When set, persisted on cleaning_tasks (used when task has no reservation). */
      guestCount?: number | null;
    }) => {
      const useAssignments = assignments && assignments.length > 0;
      const firstAssignment = useAssignments ? assignments![0] : null;

      // Load current row so we can pass along in-progress work without wiping progress.
      const { data: currentRow, error: currentErr } = await supabase
        .from("cleaning_tasks")
        .select("started_at, status, assignee_user_id, cleaner_id, assigned_type")
        .eq("id", taskId)
        .maybeSingle();
      if (currentErr) throw currentErr;

      const nextCleanerId =
        useAssignments && firstAssignment
          ? firstAssignment.assignedType === "cleaner"
            ? firstAssignment.cleanerId
            : null
          : assignedType === "owner"
            ? null
            : cleanerId;
      const nextAssigneeUserId =
        useAssignments && firstAssignment
          ? firstAssignment.assignedType === "cleaner"
            ? null
            : firstAssignment.assigneeUserId
          : assigneeUserId ?? null;
      const nextAssignedType =
        useAssignments && firstAssignment
          ? firstAssignment.assignedType
          : assignedType ?? null;

      const assigneeChanging =
        (currentRow?.assignee_user_id ?? null) !== (nextAssigneeUserId ?? null) ||
        (currentRow?.cleaner_id ?? null) !== (nextCleanerId ?? null) ||
        (currentRow?.assigned_type ?? null) !== (nextAssignedType ?? null);

      const isStarted =
        Boolean(currentRow?.started_at) || currentRow?.status === "in_progress";

      const updatePayload: Record<string, unknown> = {
        coordinator_user_id: coordinatorUserId ?? null,
        scheduled_date: scheduledDate,
        notes,
        status,
        override_check_in_time: overrideCheckInTime ?? null,
        override_check_out_time: overrideCheckOutTime ?? null,
      };
      if (includeInventoryCheck !== undefined) updatePayload.include_inventory_check = includeInventoryCheck;
      if (useAssignments && firstAssignment) {
        // Keep checklist rows + mirror primary assignee onto the task so My Tasks / runners work.
        updatePayload.checklist_id = firstAssignment.checklistId;
        updatePayload.assigned_type = firstAssignment.assignedType;
        updatePayload.cleaner_id = nextCleanerId;
        updatePayload.assignee_user_id = nextAssigneeUserId;
        updatePayload.assignment_reason =
          firstAssignment.assignedType === "cleaner" && firstAssignment.cleanerId
            ? "Owner manually assigned"
            : firstAssignment.assigneeUserId
              ? "Assigned to user"
              : null;
      } else {
        updatePayload.cleaner_id = nextCleanerId;
        updatePayload.assignee_user_id = nextAssigneeUserId;
        updatePayload.checklist_id = checklistId;
        updatePayload.assignment_reason =
          assignedType === "cleaner" && cleanerId
            ? "Owner manually assigned"
            : assigneeUserId
              ? "Assigned to user"
              : null;
        if (assignedType != null) updatePayload.assigned_type = assignedType;
      }

      // Pass along: keep visit progress; only change who owns the job.
      if (isStarted && assigneeChanging) {
        if (status === "scheduled" || !status) {
          updatePayload.status = currentRow?.status === "completed" ? "completed" : "in_progress";
        }
        updatePayload.assignment_reason = "Passed along (in progress)";
      }

      if (scheduledStartAt != null) updatePayload.scheduled_start_at = scheduledStartAt;
      if (scheduledEndAt != null) updatePayload.scheduled_end_at = scheduledEndAt;
      if (guestCount !== undefined) updatePayload.guest_count = guestCount;

      let { error } = await supabase
        .from("cleaning_tasks")
        .update(updatePayload)
        .eq("id", taskId);

      // Fallback: if team assignee is cohost-only and DB still requires property_access,
      // keep checklist assignment rows (My Tasks reads those) with main row unassigned.
      if (
        error &&
        useAssignments &&
        firstAssignment &&
        firstAssignment.assignedType === "team"
      ) {
        const fallbackPayload: Record<string, unknown> = {
          ...updatePayload,
          checklist_id: firstAssignment.checklistId,
          cleaner_id: null,
          assignee_user_id: null,
          assigned_type: "unassigned",
          assignment_reason: null,
        };
        const retry = await supabase
          .from("cleaning_tasks")
          .update(fallbackPayload)
          .eq("id", taskId);
        error = retry.error;
      }

      if (error) throw error;
      if (useAssignments) {
        await supabase.from("cleaning_task_checklist_assignments").delete().eq("cleaning_task_id", taskId);
        await supabase.from("cleaning_task_checklist_assignments").insert(
          assignments!.map((a) => ({
            cleaning_task_id: taskId,
            checklist_id: a.checklistId,
            assigned_type: a.assignedType,
            cleaner_id: a.cleanerId,
            assignee_user_id: a.assigneeUserId,
          }))
        );
      } else {
        // Legacy single-row assignment: drop per-checklist rows so we never show one
        // assignee in Task Details while another cleaner still matches `cleaning_tasks.cleaner_id`.
        await supabase.from("cleaning_task_checklist_assignments").delete().eq("cleaning_task_id", taskId);
      }
      return {
        propertyId,
        taskId,
        scheduledDate,
        scheduledStartAt,
        scheduledEndAt,
        cleanerId,
        assigneeUserId,
        assignedType,
        coordinatorUserId,
        checklistId,
        notes,
        status,
        overrideCheckInTime: overrideCheckInTime ?? null,
        overrideCheckOutTime: overrideCheckOutTime ?? null,
      };
    },
    onSuccess: (data) => {
      const {
        propertyId,
        taskId,
        scheduledDate,
        scheduledStartAt,
        scheduledEndAt,
        cleanerId,
        assigneeUserId,
        assignedType,
        coordinatorUserId,
        checklistId,
        notes,
        status,
        overrideCheckInTime,
        overrideCheckOutTime,
      } = data;
      queryClient.setQueryData<CleaningTaskWithRelations[]>(
        ["cleaning-tasks", propertyId],
        (old) => {
          if (!old) return old;
          return old.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  scheduled_date: scheduledDate,
                  ...(scheduledStartAt != null && { scheduled_start_at: scheduledStartAt }),
                  ...(scheduledEndAt != null && { scheduled_end_at: scheduledEndAt }),
                  ...(cleanerId !== undefined && { cleaner_id: cleanerId }),
                  ...(assigneeUserId !== undefined && { assignee_user_id: assigneeUserId }),
                  ...(assignedType !== undefined && { assigned_type: assignedType }),
                  ...(coordinatorUserId !== undefined && { coordinator_user_id: coordinatorUserId }),
                  ...(checklistId !== undefined && { checklist_id: checklistId }),
                  ...(notes !== undefined && { notes }),
                  ...(status !== undefined && { status }),
                  ...(overrideCheckInTime !== undefined && { override_check_in_time: overrideCheckInTime }),
                  ...(overrideCheckOutTime !== undefined && { override_check_out_time: overrideCheckOutTime }),
                }
              : t
          );
        }
      );
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["cleaning-task", taskId] });
      queryClient.invalidateQueries({ queryKey: ["property-task-checklist-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["task-checklist-assignments", taskId] });
      queryClient.invalidateQueries({ queryKey: ["reservations", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["company-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task"] });
      queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["needs-attention"] });
      queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner"] });
      queryClient.refetchQueries({ queryKey: ["cleaning-tasks", propertyId] });
      queryClient.refetchQueries({ queryKey: ["reservations", propertyId] });
    },
  });
};

/** Update only the cleaning fee (rate) for a task. Used by company to set rate for owner to pay, or by owner to set/override. */
export const useUpdateTaskCleaningFee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      propertyId,
      cleaningFee,
      companyId,
    }: {
      taskId: string;
      propertyId: string;
      cleaningFee: number | null;
      companyId?: string;
    }) => {
      const feeNum = cleaningFee === null || Number.isNaN(cleaningFee) ? null : Number(cleaningFee);
      const billableCents = feeNum != null ? Math.round(feeNum * 100) : null;
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({
          cleaning_fee: feeNum,
          billable_amount_cents: billableCents ?? 0,
        })
        .eq("id", taskId);

      if (error) throw error;
      return { taskId, propertyId, cleaningFee, companyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["cleaning-task", data.taskId] });
      queryClient.invalidateQueries({ queryKey: ["invoice-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-periods", data.propertyId] });
      if (data.companyId) {
        queryClient.invalidateQueries({ queryKey: ["company-cleaning-tasks", data.companyId] });
      }
    },
  });
};

export const useDeleteCleaningTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      propertyId,
    }: {
      taskId: string;
      propertyId: string;
    }) => {
      const { data: task, error: taskErr } = await supabase
        .from("cleaning_tasks")
        .select("preventative_maintenance_id, reservation_id")
        .eq("id", taskId)
        .single();
      if (taskErr) throw taskErr;

      const reservationId = (task as { reservation_id?: string | null } | null)?.reservation_id ?? null;

      // Linked stay: suppress + delete reservation so Sync / poll cannot recreate the turnover.
      if (reservationId) {
        const { data, error } = await supabase.rpc("delete_reservation_and_suppress", {
          p_reservation_id: reservationId,
        });
        if (error) {
          const msg = error.message || "Failed to remove reservation";
          if (/full access|only full access|not allowed/i.test(msg)) {
            throw new Error(
              "Only Full Access team members can remove reservations. Ask the account owner if this needs to be removed.",
            );
          }
          throw new Error(msg);
        }
        return { propertyId, suppressed: true, rpc: data };
      }

      const { error } = await supabase.from("cleaning_tasks").delete().eq("id", taskId);
      if (error) throw error;

      if (task?.preventative_maintenance_id) {
        await supabase
          .from("property_preventative_maintenance")
          .update({ next_scheduled_date: null })
          .eq("id", task.preventative_maintenance_id);
      }

      return { propertyId, suppressed: false };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["reservations", data.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["company-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task"] });
      queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["needs-attention"] });
      queryClient.invalidateQueries({ queryKey: ["property-preventative-maintenance", data.propertyId] });
    },
  });
};